/* Empty and error states.

   Every empty state answers three questions: what is missing, why, and what to
   do about it. An illustration with the word "Empty" under it answers none of
   them and is the most common way this component is done badly.

   Error states are separated from empty ones because they are not the same
   situation: empty means the query worked and there is nothing there, error
   means the query failed and there may well be plenty. Rendering "No providers
   found" after a network failure is a lie. */

import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, useReducedMotion } from 'motion/react'
import { CloudOff, LogIn, PackageOpen, SearchX, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EASE_OUT } from '../../design/motion'

interface EmptyStateProps {
  icon?: ReactNode
  title: string
  body?: string
  action?: ReactNode
  compact?: boolean
}

export function EmptyState({ icon, title, body, action, compact }: EmptyStateProps) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      className={`empty ${compact ? 'empty--compact' : ''}`}
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
    >
      <span className="empty-icon" aria-hidden>
        {icon ?? <PackageOpen size={26} />}
      </span>
      <h3 className="empty-title">{title}</h3>
      {body && <p className="empty-body">{body}</p>}
      {action && <div className="empty-action">{action}</div>}
    </motion.div>
  )
}

export function NoResults({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return <EmptyState icon={<SearchX size={26} />} title={title} {...(body ? { body } : {})} {...(action ? { action } : {})} />
}

interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  compact?: boolean
}

/** Reads the typed error code rather than string-matching a message, which is
 *  the only approach that survives translation.
 *
 *  Three cases, not two. `unauthorized` was falling through to the generic one,
 *  so every gated route — addresses, orders, saved — met a signed-out visitor
 *  with "Something went wrong. That did not go through. It is usually worth one
 *  more try." and a Retry button (Live-Test P1-2). None of that is true: nothing
 *  went wrong, the request did go through, and retrying it will 401 forever. The
 *  friendly copy was the problem, because it was confidently wrong about a
 *  situation with an obvious answer.
 *
 *  Retry is deliberately replaced rather than accompanied here. Offering both
 *  keeps the useless button on screen next to the useful one, and the one that
 *  does nothing is the one that looks like the safe choice. */
export function ErrorState({ error, onRetry, compact }: ErrorStateProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const code = (error as { code?: string } | null)?.code

  if (code === 'unauthorized') {
    /* Carries where they were, so signing in returns them to the page they
       asked for rather than to the home screen — the same `next` contract the
       checkout uses. `pathname + search` because a filtered order list is a
       different page from an unfiltered one. */
    const next = encodeURIComponent(`${location.pathname}${location.search}`)
    return (
      <EmptyState
        compact={compact ?? false}
        icon={<LogIn size={26} />}
        title={t('common.signInTitle')}
        body={t('common.signInBody')}
        action={
          <Link to={`/auth?next=${next}`} className="btn pri">
            {t('account.signIn')}
          </Link>
        }
      />
    )
  }

  const offline = code === 'offline'
  return (
    <EmptyState
      compact={compact ?? false}
      icon={offline ? <CloudOff size={26} /> : <TriangleAlert size={26} />}
      title={offline ? t('common.offline') : t('common.errorTitle')}
      body={offline ? t('common.offlineBody') : t('common.errorBody')}
      {...(onRetry
        ? {
            action: (
              <button type="button" className="btn ghost" onClick={onRetry}>
                {t('common.retry')}
              </button>
            ),
          }
        : {})}
    />
  )
}
